import { ILoader, ILoaderProps, composeLoaders } from './ILoaderDecorator';
import { ModuleLoadError } from '@ms/utilities-error/lib/ModuleLoadError';
import { ApiError } from '@ms/utilities-error/lib/ApiError';
import { IWithRetriesOptions, withRetries } from './withRetries';

/**
 * Wraps a loader with a version which retries the original loader with the specified options,
 * re-throwing an errors as `ModuleLoadError`.
 * @public
 *
 * @remarks
 * ```ts
 *  const moduleLoader = asModuleWithRetries(() => import('./Foo'), { retryCount: 2, retryInterval: 200 });
 *
 *  // To select an export, wrap `asModuleWithRetries` with `forModuleExport`, not the other way around.
 *  const barLoader = forModuleExport(asModuleWithRetries(() => import('./Foo')), 'bar');
 * ```
 */
export function asModuleWithRetries<TResult>(
  load: ILoader<TResult>,
  options: IAsModuleLoadOptions & IWithRetriesOptions = {}
): ILoader<TResult> {
  return composeLoaders(asModule, withRetries)(load, options);
}

/**
 * Wraps a loader with a selector which extracts an export with a given name from a loaded module.
 * @public
 *
 * @remarks
 * ```ts
 *  const barLoader = forModuleExport(() => import('./Foo'), 'bar');
 * ```
 */
export function forModuleExport<TModule, KExport extends keyof TModule>(
  load: ILoader<TModule>,
  exportName: KExport
): ILoader<TModule[KExport]> {
  return async (props: ILoaderProps = {}): Promise<TModule[KExport]> => {
    const moduleContent = await load(props);

    return moduleContent[exportName];
  };
}

/**
 * Options for wrapping a loader as a 'module load'.
 * @public
 */
export interface IAsModuleLoadOptions {
  /**
   * A 'chunk name' to use to mark the loaded module.
   * Used for debugging or error reporting only.
   */
  chunkName?: string;
  /**
   * Whether or not to permit an empty module, which may otherwise
   * be an indicator that there was a failure to initialize a module.
   */
  allowEmptyModule?: boolean;
}

/**
 * Wraps a loader with a version that re-emits any encountered errors as `ModuleLoadError`.
 * In addition, unless `allowEmptyModule` is specified, module-loads which do not emit a truthy value
 * will result in an error.
 * @public
 *
 * @remarks
 *
 * `asModule` should not be wrapped around a loader that selects an export from a module;
 * it should be wrapped only around a loader that will return all exports from the module.
 * To select an export, wrap usage of `asModule` with `forModuleExport`.
 */
export function asModule<TResult>(
  load: ILoader<TResult>,
  options: IAsModuleLoadOptions = {}
): ILoader<TResult> {
  const {
    chunkName,
    allowEmptyModule
  } = options;

  return async (props: ILoaderProps = {}) => {
    let result: TResult | undefined;

    try {
      result = await load(props);
    } catch (e) {
      if (e instanceof ApiError) {
        throw e;
      }

      throw new ModuleLoadError({
        code: 'LoadError',
        innerError: e,
        chunkName
      });
    }

    if (!result && !allowEmptyModule) {
      // If the module evaluation was `undefined`, that indicates a module initialization failure.
      throw new ModuleLoadError({
        code: 'EmptyModule',
        chunkName
      });
    }

    return result;
  };
}
